import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, MapPin, Bed, DollarSign, Home, Key, TrendingUp, Settings, ArrowRight, Calendar, Users, Building, SlidersHorizontal } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getAreas } from '@/lib/api';
import { useCurrency } from '@/contexts/CurrencyContext';

const PropertySearch = ({ variant = 'hero', onSearch }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('search');
  const [locations, setLocations] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { formatPriceShort } = useCurrency();
  const [filters, setFilters] = useState({
    location: '',
    bedrooms: '',
    minPrice: '',
    maxPrice: '',
    propertyType: '',
    ownershipType: '',
  });
  
  // Stay tab filters (short-term / nightly)
  const [stayFilters, setStayFilters] = useState({
    location: '',
    checkIn: '',
    checkOut: '',
    guests: '',
  });
  
  // Rent tab filters (long-term / monthly-yearly)
  const [rentFilters, setRentFilters] = useState({
    location: '',
    bedrooms: '',
    rentalPeriod: 'monthly', // monthly or yearly
    maxPrice: '',
  });

  const tabs = [
    { id: 'search', label: 'Search', icon: Search },
    { id: 'sell', label: 'Sell', icon: TrendingUp },
    { id: 'stay', label: 'Stay', icon: Home },
    { id: 'rent', label: 'Rent', icon: Building },
    { id: 'management', label: 'Management', icon: Settings },
  ];

  // Fetch locations from API
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await getAreas(true); // Only active areas
        setLocations(res.data.map(a => a.name));
      } catch (err) {
        // Fallback to default locations if API fails
        setLocations(['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud']);
      }
    };
    fetchLocations();
  }, []);

  const propertyTypes = [
    { value: 'villa', label: 'Villa' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'house', label: 'House' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'land', label: 'Land' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'boutique_hotel', label: 'Boutique Hotel' },
  ];

  const ownershipTypes = [
    { value: 'freehold', label: 'Freehold' },
    { value: 'leasehold', label: 'Leasehold' },
    { value: 'both', label: 'Freehold & Leasehold' },
  ];

  const bedroomOptions = [
    { value: '1', label: '1 Bedroom' },
    { value: '2', label: '2 Bedrooms' },
    { value: '3', label: '3 Bedrooms' },
    { value: '4', label: '4+ Bedrooms' },
  ];

  const priceRanges = [
    { value: '0-300000', label: `Under ${formatPriceShort(300000)}` },
    { value: '300000-500000', label: `${formatPriceShort(300000)} - ${formatPriceShort(500000)}` },
    { value: '500000-1000000', label: `${formatPriceShort(500000)} - ${formatPriceShort(1000000)}` },
    { value: '1000000-99999999', label: `${formatPriceShort(1000000)}+` },
  ];

  const monthlyPriceRanges = [
    { value: '0-2000', label: `Under ${formatPriceShort(2000)}/mo` },
    { value: '2000-5000', label: `${formatPriceShort(2000)} - ${formatPriceShort(5000)}/mo` },
    { value: '5000-10000', label: `${formatPriceShort(5000)} - ${formatPriceShort(10000)}/mo` },
    { value: '10000-99999999', label: `${formatPriceShort(10000)}+/mo` },
  ];

  const yearlyPriceRanges = [
    { value: '0-20000', label: `Under ${formatPriceShort(20000)}/yr` },
    { value: '20000-50000', label: `${formatPriceShort(20000)} - ${formatPriceShort(50000)}/yr` },
    { value: '50000-100000', label: `${formatPriceShort(50000)} - ${formatPriceShort(100000)}/yr` },
    { value: '100000-99999999', label: `${formatPriceShort(100000)}+/yr` },
  ];
  
  const guestOptions = [
    { value: '1', label: '1 Guest' },
    { value: '2', label: '2 Guests' },
    { value: '3', label: '3 Guests' },
    { value: '4', label: '4 Guests' },
    { value: '5', label: '5+ Guests' },
  ];

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (filters.location) params.set('location', filters.location);
    if (filters.bedrooms) params.set('bedrooms', filters.bedrooms);
    if (filters.propertyType) params.set('property_type', filters.propertyType);
    if (filters.ownershipType) params.set('ownership_type', filters.ownershipType);
    if (filters.minPrice) params.set('min_price', filters.minPrice);
    if (filters.maxPrice) params.set('max_price', filters.maxPrice);
    
    // Search tab = Properties for sale (freehold/leasehold)
    params.set('status', 'for_sale');

    if (onSearch) {
      onSearch({ ...filters, status: 'for_sale' });
    } else {
      navigate(`/properties?${params.toString()}`);
    }
  };
  
  const handleStaySearch = () => {
    const params = new URLSearchParams();
    if (stayFilters.location) params.set('location', stayFilters.location);
    if (stayFilters.checkIn) params.set('check_in', stayFilters.checkIn);
    if (stayFilters.checkOut) params.set('check_out', stayFilters.checkOut);
    if (stayFilters.guests) params.set('guests', stayFilters.guests);
    params.set('rental_type', 'nightly');
    
    navigate(`/stays?${params.toString()}`);
  };

  const handleRentSearch = () => {
    const params = new URLSearchParams();
    if (rentFilters.location) params.set('location', rentFilters.location);
    if (rentFilters.bedrooms) params.set('bedrooms', rentFilters.bedrooms);
    if (rentFilters.maxPrice) params.set('max_price', rentFilters.maxPrice);
    params.set('rental_type', rentFilters.rentalPeriod);
    
    navigate(`/rentals?${params.toString()}`);
  };

  const handlePriceChange = (value) => {
    const [min, max] = value.split('-');
    setFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }));
  };

  const isHero = variant === 'hero';

  // Render CTA content for Sell and Management tabs
  const renderCTAContent = () => {
    if (activeTab === 'sell') {
      return (
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 py-4">
          <div className="text-center md:text-left">
            <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">
              What's Your Property Worth?
            </h3>
            <p className="font-body text-charcoal-light text-sm max-w-md">
              Get a free, no-obligation valuation from our local experts within 24-48 hours.
            </p>
          </div>
          <Link
            to="/sell"
            className="flex-shrink-0 bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full px-8 py-3 font-medium inline-flex items-center gap-2 transition-all duration-300"
            data-testid="sell-cta-btn"
          >
            Get Free Valuation
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    }

    if (activeTab === 'management') {
      return (
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 py-4">
          <div className="text-center md:text-left">
            <h3 className="font-heading text-xl font-semibold text-charcoal mb-2">
              Professional Property Management
            </h3>
            <p className="font-body text-charcoal-light text-sm max-w-md">
              Let us manage your villa. From guest services to maintenance, we handle everything while you earn.
            </p>
          </div>
          <Link
            to="/property-management"
            className="flex-shrink-0 bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full px-8 py-3 font-medium inline-flex items-center gap-2 transition-all duration-300"
            data-testid="management-cta-btn"
          >
            Start Earning Now
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      );
    }

    return null;
  };

  // Check if current tab shows search filters or other content
  const showSearchFilters = activeTab === 'search';
  const showStayFilters = activeTab === 'stay';
  const showRentFilters = activeTab === 'rent';
  const showCTA = activeTab === 'sell' || activeTab === 'management';

  // Render Stay tab content with Airbnb-style filters
  const renderStayFilters = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
      {/* Location */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
          <MapPin className="w-4 h-4" />
          Where
        </label>
        <Select
          value={stayFilters.location}
          onValueChange={(value) => setStayFilters(prev => ({ ...prev, location: value }))}
        >
          <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="stay-location">
            <SelectValue placeholder="Search destinations" />
          </SelectTrigger>
          <SelectContent>
            {locations.map(loc => (
              <SelectItem key={loc} value={loc}>{loc}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Check In */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
          <Calendar className="w-4 h-4" />
          Check In
        </label>
        <Input
          type="date"
          value={stayFilters.checkIn}
          onChange={(e) => setStayFilters(prev => ({ ...prev, checkIn: e.target.value }))}
          className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle"
          data-testid="stay-checkin"
        />
      </div>

      {/* Check Out */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
          <Calendar className="w-4 h-4" />
          Check Out
        </label>
        <Input
          type="date"
          value={stayFilters.checkOut}
          onChange={(e) => setStayFilters(prev => ({ ...prev, checkOut: e.target.value }))}
          className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle"
          data-testid="stay-checkout"
        />
      </div>

      {/* Guests */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
          <Users className="w-4 h-4" />
          Guests
        </label>
        <Select
          value={stayFilters.guests}
          onValueChange={(value) => setStayFilters(prev => ({ ...prev, guests: value }))}
        >
          <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="stay-guests">
            <SelectValue placeholder="Add guests" />
          </SelectTrigger>
          <SelectContent>
            {guestOptions.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Search Button */}
      <div className="flex items-end">
        <Button
          onClick={handleStaySearch}
          className="w-full bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full h-10 font-medium"
          data-testid="stay-search-btn"
        >
          <Search className="w-4 h-4 mr-2" />
          Search Stays
        </Button>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.5 }}
      className={`${
        isHero
          ? 'bg-white/95 backdrop-blur-md rounded-2xl shadow-floating'
          : 'bg-warm-sand rounded-2xl p-6'
      }`}
      data-testid="property-search"
    >
      {/* Tabs - Only show in hero variant */}
      {isHero && (
        <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 sm:px-6 py-4 font-body font-medium text-sm transition-all duration-200 border-b-2 -mb-[1px] whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-ubud-jungle border-ubud-jungle'
                  : 'text-charcoal-light border-transparent hover:text-charcoal hover:border-gray-300'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Content area */}
      <div className={isHero ? 'p-6' : ''}>
        {/* Show CTA for sell/management tabs */}
        {isHero && showCTA && renderCTAContent()}
        
        {/* Show Stay filters for stay tab (short-term nightly rentals) */}
        {isHero && showStayFilters && renderStayFilters()}

        {/* Show Rent filters for rent tab (long-term monthly/yearly) */}
        {isHero && showRentFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Location */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                <MapPin className="w-4 h-4" />
                Location
              </label>
              <Select
                value={rentFilters.location}
                onValueChange={(value) => setRentFilters(prev => ({ ...prev, location: value }))}
              >
                <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="rent-location">
                  <SelectValue placeholder="Any location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map(loc => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bedrooms */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                <Bed className="w-4 h-4" />
                Bedrooms
              </label>
              <Select
                value={rentFilters.bedrooms}
                onValueChange={(value) => setRentFilters(prev => ({ ...prev, bedrooms: value }))}
              >
                <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="rent-bedrooms">
                  <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                  {bedroomOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Rental Period */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                <Calendar className="w-4 h-4" />
                Rental Period
              </label>
              <Select
                value={rentFilters.rentalPeriod}
                onValueChange={(value) => setRentFilters(prev => ({ ...prev, rentalPeriod: value, maxPrice: '' }))}
              >
                <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="rent-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Price Range */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                <DollarSign className="w-4 h-4" />
                Price Range
              </label>
              <Select
                value={rentFilters.maxPrice}
                onValueChange={(value) => setRentFilters(prev => ({ ...prev, maxPrice: value }))}
              >
                <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="rent-price">
                  <SelectValue placeholder="Any price" />
                </SelectTrigger>
                <SelectContent>
                  {(rentFilters.rentalPeriod === 'monthly' ? monthlyPriceRanges : yearlyPriceRanges).map(range => (
                    <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <Button
                onClick={handleRentSearch}
                className="w-full bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full h-10 font-medium"
                data-testid="rent-search-btn"
              >
                <Search className="w-4 h-4 mr-2" />
                Search Rentals
              </Button>
            </div>
          </div>
        )}

        {/* Search filters for search tab (properties for sale) */}
        {(!isHero || showSearchFilters) && (
          <>
            {/* Mobile: Compact search */}
            <div className="md:hidden">
              <div className="flex gap-2">
                <Select
                  value={filters.location}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, location: value }))}
                >
                  <SelectTrigger className="bg-white border-gray-200 flex-1" data-testid="search-location-mobile">
                    <MapPin className="w-4 h-4 mr-2 text-charcoal-light" />
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  onClick={() => setShowMobileFilters(!showMobileFilters)}
                  className="bg-white hover:bg-gray-50 border-gray-200 px-4"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
                <Button
                  onClick={handleSearch}
                  className="bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full px-4"
                  data-testid="search-btn-mobile"
                >
                  <Search className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Mobile Expanded Filters */}
              {showMobileFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-3 pt-3 border-t border-gray-200 space-y-3"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <Select
                      value={filters.bedrooms}
                      onValueChange={(value) => setFilters(prev => ({ ...prev, bedrooms: value }))}
                    >
                      <SelectTrigger className="bg-white border-gray-200 h-9">
                        <SelectValue placeholder="Bedrooms" />
                      </SelectTrigger>
                      <SelectContent>
                        {bedroomOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={filters.minPrice && filters.maxPrice ? `${filters.minPrice}-${filters.maxPrice}` : ''}
                      onValueChange={handlePriceChange}
                    >
                      <SelectTrigger className="bg-white border-gray-200 h-9">
                        <SelectValue placeholder="Price" />
                      </SelectTrigger>
                      <SelectContent>
                        {priceRanges.map(range => (
                          <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Select
                    value={filters.propertyType}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, propertyType: value }))}
                  >
                    <SelectTrigger className="bg-white border-gray-200 h-9">
                      <SelectValue placeholder="Property Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </motion.div>
              )}
            </div>

            {/* Desktop: Full search filters */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Location */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <MapPin className="w-4 h-4" />
                  Location
                </label>
                <Select
                  value={filters.location}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, location: value }))}
                >
                  <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="search-location">
                    <SelectValue placeholder="Any location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bedrooms */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <Bed className="w-4 h-4" />
                  Bedrooms
                </label>
                <Select
                  value={filters.bedrooms}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, bedrooms: value }))}
                >
                  <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="search-bedrooms">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    {bedroomOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <DollarSign className="w-4 h-4" />
                  Price Range
                </label>
                <Select
                  value={filters.minPrice && filters.maxPrice ? `${filters.minPrice}-${filters.maxPrice}` : ''}
                  onValueChange={handlePriceChange}
                >
                  <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="search-price">
                    <SelectValue placeholder="Any price" />
                  </SelectTrigger>
                  <SelectContent>
                    {priceRanges.map(range => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Type */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-charcoal-light">
                  <Home className="w-4 h-4" />
                  Property Type
                </label>
                <Select
                  value={filters.propertyType}
                  onValueChange={(value) => setFilters(prev => ({ ...prev, propertyType: value }))}
                >
                  <SelectTrigger className="bg-white border-gray-200 focus:border-ubud-jungle focus:ring-ubud-jungle" data-testid="search-type">
                    <SelectValue placeholder="Any type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <Button
                  onClick={handleSearch}
                  className="w-full bg-ubud-jungle hover:bg-ubud-jungle-dark text-white rounded-full h-10 font-medium"
                  data-testid="search-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Properties
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default PropertySearch;
